import {ContextMenuButton} from '@app/web-player/context-dialog/context-dialog-layout';
import {Trans} from '@ui/i18n/trans';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {tracksToMediaItems} from '@app/web-player/tracks/utils/track-to-media-item';
import {queueGroupId} from '@app/web-player/queue-group-id';
import {Track} from '@app/web-player/tracks/track';
import {Artist} from '@app/web-player/artists/artist';
import {Playlist} from '@app/web-player/playlists/playlist';
import {usePlayerActions} from '@common/player/hooks/use-player-actions';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {Album} from '@app/web-player/albums/album';
import {apiClient} from '@common/http/query-client';
import {getUserRolesAccess} from '@common/auth/user-roles-access';

type MediaItem = Track[] | Album | Artist | Playlist;

interface AddToQueueButtonProps {
  item: MediaItem;
  loadTracks: () => Promise<Track[]>;
}
export function AddToQueueButton({item, loadTracks}: AddToQueueButtonProps) {
  const {close: closeMenu} = useDialogContext();
  const player = usePlayerActions();
  const controlsLocked = usePlayerStore(s => s.controlsLocked);

  return (
    <ContextMenuButton
      disabled={controlsLocked}
      onClick={async () => {
        if (controlsLocked) {
          return;
        }
        closeMenu();
        const tracks = await loadTracks();
        const itemQueueId = Array.isArray(item) ? undefined : queueGroupId(item);
        const {userAccess} = getUserRolesAccess();
        const hasFullAlbumControl =
          userAccess.medias.play.base &&
          parseRestrictionBool(userAccess.medias.play.canQueueFullAlbum);
        const shouldAutoMixAlbum = !hasFullAlbumControl;

        if (isAlbum(item) && shouldAutoMixAlbum && tracks.length) {
          const {keptTracks, recommendedTracks} = await buildAutoMixTracks(tracks);
          player.appendToQueue([
            ...tracksToMediaItems(keptTracks, itemQueueId, item),
            ...tracksToMediaItems(recommendedTracks),
          ]);
          return;
        }

        player.appendToQueue(tracksToMediaItems(tracks, itemQueueId));
      }}
    >
      <Trans message="Add to queue" />
    </ContextMenuButton>
  );
}

function isAlbum(item: MediaItem): item is Album {
  return !Array.isArray(item) && item.model_type === 'album';
}

function parseRestrictionBool(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return !['false', '0', 'off', 'no'].includes(normalized);
  }
  return Boolean(value);
}

async function buildAutoMixTracks(albumTracks: Track[]): Promise<{
  keptTracks: Track[];
  recommendedTracks: Track[];
}> {
  const keepCount = Math.min(
    albumTracks.length,
    Math.max(3, Math.round(albumTracks.length * 0.1)),
  );
  const keptTracks = getRandomTracks(albumTracks, keepCount);
  const neededRecommendations = Math.max(0, albumTracks.length - keptTracks.length);

  if (!neededRecommendations) {
    return {
      keptTracks,
      recommendedTracks: [],
    };
  }

  const recommendations = await fetchTrackRecommendations(
    keptTracks[0] ?? albumTracks[0],
  );
  const keptTrackIds = new Set(keptTracks.map(track => track.id));
  const dedupedRecommendations = recommendations
    .filter(track => !keptTrackIds.has(track.id))
    .slice(0, neededRecommendations);

  return {
    keptTracks,
    recommendedTracks: dedupedRecommendations,
  };
}

async function fetchTrackRecommendations(seedTrack?: Track): Promise<Track[]> {
  if (!seedTrack?.id) {
    return [];
  }

  try {
    const response = await apiClient.get<{recommendations?: Track[]}>(
      `radio/track/${seedTrack.id}`,
      {
        params: {
          context: 'player_queue',
          limit: 10,
        },
      },
    );
    return response.data?.recommendations ?? [];
  } catch {
    return [];
  }
}

function getRandomTracks(tracks: Track[], count: number): Track[] {
  if (count >= tracks.length) {
    return [...tracks];
  }

  const shuffled = [...tracks];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }
  return shuffled.slice(0, count);
}
